import React, { useState } from 'react'

import { useAnalysisActions } from 'redux/actions/Analyses'
import { useTranslation } from 'language/translator'

import Button from 'components_new/atoms/Button'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import StatusBox from 'components_new/molecules/StatusBox'

import TasksTable from 'components_new/organisms/tasks/TasksTable'
import { TAnalysis } from 'redux/hooks/Analyses'

interface CreateTasksFromAnalysisDialogProps {
  analysis: TAnalysis
  onClose: () => void
  onCreate: () => void
  open: boolean
}

/**
 * Shown before an analysis is accepted. Lists one row per selected property;
 * each becomes its own activity. The owner defaults to the analysis's
 * responsible account and can be changed per row.
 */
const CreateTasksFromAnalysisDialog = (
  props: CreateTasksFromAnalysisDialogProps
) => {
  const { analysis, onClose, onCreate, open } = props

  const { t } = useTranslation()
  const { tryAcceptAnalysis } = useAnalysisActions()

  const properties = analysis.qualified_properties.filter(
    (item) => item.selected
  )

  // Keyed by real estate name, the only identity an analysis object carries.
  const [ownerOverrides, setOwnerOverrides] = useState<
    Record<string, string | null>
  >({})
  const [submitting, setSubmitting] = useState(false)

  const isEmpty = properties.length === 0

  // The row's chosen owner, else the analysis's responsible account.
  const ownerOf = (realEstate: string) =>
    realEstate in ownerOverrides
      ? ownerOverrides[realEstate]
      : analysis.responsible_account_id ?? null

  // The activity is named here; the server sets no default.
  const titleOf = (realEstate: string) => `${analysis.name} – ${realEstate}`

  const handleCreate = () => {
    if (isEmpty) {
      return
    }

    setSubmitting(true)

    tryAcceptAnalysis(
      analysis.id,
      properties.map((item) => ({
        real_estate: item.name,
        title: titleOf(item.name),
        responsible_account_id: ownerOf(item.name)
      })),
      (err) => {
        setSubmitting(false)

        if (!err) {
          onCreate()
        }
      }
    )
  }

  return (
    <SimpleDialog
      actions={
        <>
          <Button disabled={submitting} onClick={onClose} variant="outlined">
            {t('global', 'cancel')}
          </Button>
          <Button
            disabled={isEmpty || submitting}
            onClick={handleCreate}
            variant="contained"
          >
            {t('analyses', 'create_activities_submit')}
          </Button>
        </>
      }
      maxWidth="md"
      onClose={onClose}
      open={open}
      title={t('analyses', 'create_activities_title')}
    >
      <StatusBox
        severity="info"
        sx={{ mb: 2 }}
        title={t('analyses', 'create_activities_info_title')}
      >
        {t('analyses', 'create_activities_info_body')}
      </StatusBox>

      {isEmpty ? (
        <EmptyPlaceholder
          background={true}
          body={t('analyses', 'create_activities_empty')}
          icon="ApartmentOutlined"
          size="small"
          title={t('analyses', 'create_activities_empty_title')}
        />
      ) : (
        <TasksTable
          maxHeight={400}
          items={properties.map((item) => ({
            // $TODO:
            created_at: '2026-01-01',
            metric: analysis.metric,
            analysis: analysis,
            metric_filter_attribute_id: analysis.metric_filter_attribute_id,
            metric_filter_value: item.name,
            noi_change: item.noi_change,
            responsible_account_id: ownerOf(item.name),
            source_type: 'ANALYSIS',
            start_value: item.start_value,
            status: 'TODO',
            target_value: item.target_value,
            title: titleOf(item.name),
            // ...
            completed_date: null,
            started_date: null,
            created_by: '',
            description: null,
            id: '',
            group_ids: [],
            key_metric_id: null,
            parent_task_id: null,
            sub_tasks: [],
            source_id: null,
            updated_at: '2026-01-01'
          }))}
          onChangeOwner={(item, value) =>
            setOwnerOverrides((prev) => ({
              ...prev,
              [item.metric_filter_value ?? '']: value
            }))
          }
        />
      )}
    </SimpleDialog>
  )
}

export default CreateTasksFromAnalysisDialog
